import apiClient from '@/services/api'
import { Modal } from 'ant-design-vue'
import moment from 'moment-timezone'
import { prepareFilterWithEmployeeLegalEntities } from '@/utils/payment-type-legal-entity-filter'
import { getAppliedRulesReviewSuggestions } from '@/modules/authorizations/utils/applied-rules-review'
import {
  formatPayCodeDateForPayload,
  getWorkDayStartTime,
  normalizePayCodeTime
} from '@/modules/shifts/utils/pay-code-time'

export default {
  async addSeveralShifts({ rootState }, { shifts, autoSign }) {
    const { data } = await apiClient.post(`/api/shift/manually_entered/several/create`, {
      project_id: rootState.applicationState.currentProject.id,
      position: {
        latitude: 0,
        longitude: 0,
        altitude: 0
      },
      associate_shifts: shifts,
      auto_sign: autoSign
    })

    return data
  },

  async checkShiftConflict({ rootState }, { associateNumber, start, end, shiftId }) {
    const { data } = await apiClient.post(`/api/shift/conflict/check`, {
      project_id: rootState.applicationState.currentProject.id,
      associate_number: associateNumber,
      start,
      end,
      shift_id: shiftId,
      check_pay_code: true
    })

    return data
  },

  /**
   * Fetches authorizations for a specific shift and commits the data to the Vuex store.
   *
   * @param {Object} payload - Contains parameters for the request.
   * @param {number} payload.shift_id - The unique identifier of the shift.
   * @throws {string} Error message if an error occurs or the API returns a non-zero error code.
   */
  async fetchShiftAuthorizations({ commit }, { shift_id }) {
    try {
      const { data } = await apiClient.post('/api/authorization/shift/get_all', {
        shift_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setShiftAuthorizations', data.auths)

      return data.auths
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Deletes an employee shift by sending a request to the backend.
   *
   * @param {Object} payload - Contains parameters for the request.
   * @param {number} payload.shift_id - The unique identifier of the shift.
   * @throws {string} Error message if an error occurs or the API returns a non-zero error code.
   */
  async deleteShift({ rootState }, { shift_id }) {
    try {
      const { data } = await apiClient.post('/api/shift/delete', {
        shift_id,
        project_id: rootState.applicationState.currentProject.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async postShiftBulk(_, { ids, action }) {
    try {
      const { data } = await apiClient.post('/api/shift/bulk_action', {
        ids,
        action
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async deleteAuthorization(_, { id }) {
    try {
      const { data } = await apiClient.delete(`/api/shift/authorization/${id}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async acceptShiftAuthorization({ rootState, dispatch }, { auth_ids, comment, review_context = [] }) {
    const reviewContext = review_context.map(({ authorization, associate }) => ({
      authorization: { ...authorization, history: { ...authorization.history } },
      associate: { ...associate }
    }))
    const { data: costData } = await apiClient.post('/api/authorization/check_cost', {
      ids: auth_ids
    })

    const performAccept = async () => {
      const { data } = await apiClient.post('/api/authorization/accept', {
        auth_ids,
        comment,
        author_id: rootState.applicationState.associate.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg)
      }

      try {
        const suggestions = getAppliedRulesReviewSuggestions(
          reviewContext,
          data.authorizations,
          rootState.applicationState.extendedShiftAuthTypes
        )
        if (suggestions.length) {
          const currentReview = rootState.modals.modals.appliedRulesReview
          await dispatch(
            'modals/openModal',
            {
              type: 'appliedRulesReview',
              data: {
                visible: true,
                suggestions: [...(currentReview.visible ? currentReview.suggestions : []), ...suggestions]
              }
            },
            { root: true }
          )
        }
      } catch (_) {
        // Reviewing rules is optional; the approval has already been saved.
      }

      return data.authorizations
    }

    const total = Object.values(costData.total).reduce((acc, curr) => (acc += curr), 0) / 100

    if (total > 0) {
      return new Promise((resolve, reject) => {
        Modal.confirm({
          title: 'Accept violations?',
          content: `Accepting these violations will cost the company $${total.toFixed(2)}.`,
          onOk: async () => {
            try {
              const authorizations = await performAccept()
              resolve({ cancelling: false, authorizations })
            } catch (error) {
              reject(error)
            }
          },
          onCancel: () => {
            resolve({ cancelling: true, authorizations: [] })
          }
        })
      })
    } else {
      const authorizations = await performAccept()
      return { cancelling: false, authorizations }
    }
  },

  async declineShiftAuthorization({ rootState }, { auth_ids, comment, params }) {
    try {
      const { data } = await apiClient.post('/api/authorization/decline', {
        auth_ids,
        comment,
        author_id: rootState.applicationState.associate.id,
        ...params
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.authorizations
    } catch (error) {
      throw error.message
    }
  },
  /**
   * Fetches a paginated list of shifts with optional filters.
   *
   * @param {Object} payload - Contains parameters for the request.
   * @param {number} [payload.page=1] - Page number for pagination.
   * @param {number} [payload.size=10] - Number of items per page.
   * @param {Object} [payload.filter={}] - Optional filter object to refine the search.
   * @returns {Object} Data containing the list of shifts and pagination info.
   * @throws {string} Error message if an error occurs or the API returns a non-zero error code.
   */
  async getShifts(
    { rootState },
    { page = 1, size = 10, filter = {}, cancelPreviousRequests = true, fields, requestGroup }
  ) {
    const manager_scopes = rootState.applicationState.associate.manager_scopes
    const currentPaymentType = rootState.applicationState.currentPaymentTypeName
    const paymentTypes = currentPaymentType === 'All' ? filter.payment_types : [currentPaymentType.toLowerCase()]
    const normalizedFilter = prepareFilterWithEmployeeLegalEntities({
      filter,
      paymentTypeKey: 'payment_types',
      legalEntityKey: 'legal_entities',
      paymentTypes
    })

    try {
      const { data } = await apiClient.post(
        `/api/shift/get_all?fields=${
          fields ||
          '{-signature,-shift_confirmation_form,-tasks,-parent_shift_id,-note,-files,-modified_by_admin,-mileages,external_payment_info{*,-calculations}}'
        }`,
        {
          project_id: rootState.applicationState.currentProject.id,
          page,
          size,
          filter: {
            ...normalizedFilter,
            shift_statuses: filter.shift_statuses?.length ? filter.shift_statuses : undefined,
            shift_payment_statuses: filter.shift_payment_statuses?.length ? filter.shift_payment_statuses : undefined,
            date_range: {
              start: filter.start_date,
              end: filter.end_date
            },
            staffing: filter.staffing === 'internal' ? false : filter.staffing === 'external' ? true : null,
            manually_created: filter.manually_created && true,
            with_pay_codes: filter.with_pay_codes && true,
            adjusted: filter.adjusted && true,
            zero_rate: filter.zero_rate && true,
            orientation_period: filter.orientation_period && true,
            training_period: filter.training_period && true,
            attached_pay_code: filter.attached_pay_code ? JSON.parse(filter.attached_pay_code) : undefined,
            payment_types: paymentTypes,
            shift_ids: filter.shift_ids?.length ? filter.shift_ids : undefined,
            manager_scopes
          }
        },
        {
          cancelPreviousRequests: cancelPreviousRequests,
          requestGroup
        }
      )

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getShiftsById(_, { ids = [], cancelPreviousRequests = true }) {
    try {
      const { data } = await apiClient.get('/api/shift/by_ids', {
        params: { ids: ids },
        cancelPreviousRequests
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getPayCodes({ rootState }, { page = 1, size = 10, filter = {}, cancelPreviousRequests = true }) {
    try {
      const { data } = await apiClient.post(
        'api/shift/pay_code/get_all',
        {
          project_id: rootState.applicationState.currentProject.id,
          page,
          size,
          ...filter
        },
        {
          cancelPreviousRequests
        }
      )

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getAuthsByShifts({ rootState }, { page = 1, size = 10, filter = {}, cancelPreviousRequests = true }) {
    const currentPaymentType = rootState.applicationState.currentPaymentTypeName
    const manager_scopes = rootState.applicationState.associate.manager_scopes

    const { start, end, payment_types, assigned_to_me, ...rest } = filter
    const resolvedPaymentTypes = currentPaymentType === 'All' ? payment_types : [currentPaymentType.toLowerCase()]
    const normalizedFilter = prepareFilterWithEmployeeLegalEntities({
      filter: rest,
      paymentTypeKey: 'payment_types',
      legalEntityKey: 'legal_entities',
      paymentTypes: resolvedPaymentTypes
    })

    try {
      const { data } = await apiClient.post(
        'api/authorization/project/get_all',
        {
          project_id: rootState.applicationState.currentProject.id,
          page,
          size,
          filter: {
            ...normalizedFilter,
            date_range: {
              start: start,
              end: end
            },
            assigned_associate_id: assigned_to_me && rootState.applicationState.associate.id,
            manager_scopes,
            payment_types: resolvedPaymentTypes,
            manually_created: filter.manually_created && true,
            with_pay_codes: filter.with_pay_codes && true,
            adjusted: filter.adjusted && true,
            resolved_by_associate:
              filter.resolved_by_associate !== undefined ? JSON.parse(filter.resolved_by_associate) : null,
            attached_pay_code: filter.attached_pay_code ? JSON.parse(filter.attached_pay_code) : undefined
          }
        },
        {
          cancelPreviousRequests
        }
      )

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches shift details along with authorizations by shift ID and commits the result.
   *
   * @param {Object} payload - Contains parameters for the request.
   * @param {number} payload.shift_id - The unique identifier of the shift.
   * @throws {string} Error message if an error occurs or the API returns a non-zero error code.
   */
  async getShiftWithAuth({ dispatch, rootState }, { shift_id }) {
    try {
      const { data } = await apiClient.post('/api/shift/get_with_auth', {
        shift_id,
        project_id: rootState.applicationState.currentProject.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      const updatedShift = { ...data.authorizations_by_shift }

      const timeZone = rootState.applicationState.timeZone

      const timeFields = [
        'scheduled_start',
        'scheduled_end',
        'work_start',
        'work_end',
        'lunch_start',
        'lunch_end',
        'second_lunch_start',
        'second_lunch_end',
        'actual_lunch_end',
        'actual_lunch_start',
        'actual_second_lunch_end',
        'actual_second_lunch_start',
        'actual_work_end',
        'actual_work_start'
      ]

      timeFields.forEach(field => {
        if (updatedShift.shift[field]) {
          updatedShift.shift[field] = moment.utc(updatedShift.shift[field]).tz(timeZone)
        }
      })

      const estimatedLunches = await dispatch('getEstimatedLunches', {
        associate_number: updatedShift.shift.internal_payment_info.associate.associate_number,
        shift_start: updatedShift.shift.scheduled_start,
        shift_end: updatedShift.shift.scheduled_end,
        lunch_waived: false,
        project_id: rootState.applicationState.currentProject.id
      })

      return {
        ...updatedShift,
        shift: {
          ...updatedShift.shift,
          scheduled_lunch_start: estimatedLunches?.estimated_lunch_start,
          scheduled_lunch_end: estimatedLunches?.estimated_lunch_end,
          scheduled_second_lunch_end: estimatedLunches?.estimated_second_lunch_end,
          scheduled_second_lunch_start: estimatedLunches?.estimated_second_lunch_start
        }
      }
    } catch (error) {
      throw error.message ?? error
    }
  },

  async getAllShiftInfo({ commit, dispatch, rootState }, { shift_id }) {
    try {
      const shiftResponse = await apiClient.post('/api/shift/get', {
        shift_id
      })

      const { data } = shiftResponse

      const associateResponse = await apiClient.post('/api/associate/get', {
        project_id: data.shift.internal_payment_info.project.id,
        associate_number: data.shift.internal_payment_info.associate.associate_number
      })

      const { data: associateData } = associateResponse

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      if (associateData.error_code && associateData.error_code !== '0') {
        throw new Error(`${associateData.msg} `)
      }

      const updatedShift = {
        shift: {
          ...data.shift,
          project: data.shift.internal_payment_info.project
        },
        associate: associateResponse.data.associate
      }

      const timeZone = rootState.applicationState.timeZone

      const timeFields = [
        'scheduled_start',
        'scheduled_end',
        'work_start',
        'work_end',
        'lunch_start',
        'lunch_end',
        'second_lunch_start',
        'second_lunch_end',
        'actual_lunch_end',
        'actual_lunch_start',
        'actual_second_lunch_end',
        'actual_second_lunch_start',
        'actual_work_end',
        'actual_work_start'
      ]

      timeFields.forEach(field => {
        if (updatedShift.shift[field]) {
          updatedShift.shift[field] = moment.utc(updatedShift.shift[field]).tz(timeZone)
        }
      })

      const estimatedLunches = await dispatch('getEstimatedLunches', {
        associate_number: data.shift.internal_payment_info.associate.associate_number,
        shift_start: updatedShift.shift.scheduled_start,
        shift_end: updatedShift.shift.scheduled_end,
        lunch_waived: false,
        project_id: updatedShift.shift.project.id
      })

      commit('setShift', {
        ...updatedShift,
        shift: {
          ...updatedShift.shift,
          scheduled_lunch_start: estimatedLunches?.estimated_lunch_start,
          scheduled_lunch_end: estimatedLunches?.estimated_lunch_end,
          scheduled_second_lunch_end: estimatedLunches?.estimated_second_lunch_end,
          scheduled_second_lunch_start: estimatedLunches?.estimated_second_lunch_start
        }
      })
    } catch (error) {
      throw error.message ?? error
    }
  },

  async getShift(_, { shift_id }) {
    try {
      const { data } = await apiClient.post('/api/shift/get', {
        shift_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return { ...data.shift, project: data.project }
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches the pay code for a specific shift.
   *
   * @param {Object} payload - Contains parameters for the request.
   * @param {number} payload.shift_id - The unique identifier of the shift.
   * @throws {string} Error message if an error occurs or the API returns a non-zero error code.
   */
  async getPayCode(_, { shift_id }) {
    try {
      const { data } = await apiClient.post('/api/shift/pay_code/get', {
        shift_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches the list of pay codes for a specific shift and commits them to the Vuex store.
   *
   * @param {Object} payload - Contains parameters for the request.
   * @param {number} payload.shift_id - The unique identifier of the shift.
   * @throws {string} Error message if an error occurs or the API returns a non-zero error code.
   */
  async getShiftPayCodes({ commit }, { shift_id }) {
    try {
      const { data } = await apiClient.post('/api/shift/pay_codes/get', {
        shift_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setShiftPayCodes', data.pay_codes)
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches calculation details for a specific shift and commits them to the Vuex store.
   *
   * @param {Object} payload - Contains parameters for the request.
   * @param {number} payload.shift_id - The unique identifier of the shift.
   * @throws {string} Error message if an error occurs or the API returns a non-zero error code.
   */
  async getShiftCalculations({ commit }, { shift_id, project_id }) {
    try {
      const { data } = await apiClient.post('/api/shift/calculations/get', {
        shift_id,
        project_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.calculations
    } catch (error) {
      throw error.message
    }
  },

  async getShiftPayments({ commit }, { shift_id }) {
    try {
      const { data } = await apiClient.post('/api/shift/payment/get', {
        shift_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setShiftPayments', data.payments)
    } catch (error) {
      throw error.message
    }
  },

  async getShiftActions({ commit }, { shift_id }) {
    try {
      const { data } = await apiClient.post('/api/shift/action/get_all', {
        shift_ids: Array.isArray(shift_id) ? shift_id : [shift_id]
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the list of locations.'} `)
      }

      const records = (data.shift_actions ?? []).flatMap(item => item.records ?? [])

      commit('setShiftActions', records)

      return records
    } catch (error) {
      throw error.message
    }
  },

  async createPayCode({ rootState }, { dates, associates, pay_code, ready_for_pay_status, time }) {
    try {
      const workDayStart = getWorkDayStartTime(rootState.applicationState.currentProject?.payment_settings)
      const normalizedTime = normalizePayCodeTime(time, workDayStart)
      const formattedDates = dates.map(date =>
        formatPayCodeDateForPayload(date, normalizedTime, workDayStart, rootState.applicationState.timeZone)
      )

      const { data } = await apiClient.post('/api/shift/pay_code/create', {
        project_id: rootState.applicationState.currentProject.id,
        ready_for_pay_status,
        pay_code_shift_form: {
          dates: formattedDates,
          associates: associates.map(item => ({ associate_number: item })),
          pay_code,
          time: normalizedTime
        }
      })

      if (data.error_code && data.error_code !== '0' && !data.shifts.length) {
        throw new Error(`${data.msg ?? 'An error occurred while creating pay code.'} `)
      } else if (data.error_code && data.error_code !== '0' && data.shifts.length) {
        return data.shifts
      }

      return null
    } catch (error) {
      throw error.message
    }
  },

  async attachPayCode({ rootState }, { pay_code, shift_ids, ready_for_pay_status }) {
    try {
      const { data } = await apiClient.post('/api/shift/pay_code/attach', {
        project_id: rootState.applicationState.currentProject.id,
        author_id: rootState.applicationState.associate.id,
        pay_code,
        shift_ids,
        ready_for_pay_status
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while creating pay code.'} `)
      }
    } catch (error) {
      throw error.message
    }
  },

  async updatePayCode({ rootState }, { pay_code, shift_id, ready_for_pay_status, scheduled_start }) {
    try {
      const { data } = await apiClient.post('/api/shift/pay_code/update', {
        project_id: rootState.applicationState.currentProject.id,
        pay_code,
        shift_id,
        ready_for_pay_status,
        scheduled_start
      })

      if (data.error_code && data.error_code !== '0' && !data.shifts.length) {
        throw new Error(`${data.msg ?? 'An error occurred while creating pay code.'} `)
      } else if (data.error_code && data.error_code !== '0' && data.shifts.length) {
        return data.shifts
      }

      return null
    } catch (error) {
      throw error.message
    }
  },

  async postHolidayPay({ rootState }, { filters, date, time }) {
    try {
      const workDayStart = getWorkDayStartTime(rootState.applicationState.currentProject?.payment_settings)

      const { data } = await apiClient.post('/api/shift/pay_code/holiday', {
        author_id: rootState.applicationState.associate.id,
        filters,
        date,
        time: normalizePayCodeTime(time, workDayStart)
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg)
      }
    } catch (error) {
      throw error.message
    }
  },

  async postSignShift(_, { shift_id, version_id, signature_id, explanation }) {
    try {
      const { data } = await apiClient.post(`/api/shift/${shift_id}/version/${version_id}/sign`, {
        signature: {
          id: signature_id
        },
        create_context: true,
        ...(explanation !== undefined ? { explanation } : {})
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while signing shift.'} `)
      }
    } catch (error) {
      throw error.message
    }
  },

  updateShiftField({ commit }, payload) {
    commit('UPDATE_SHIFT_FIELD', payload)
  },

  setShiftFields({ commit }, payload) {
    commit('SET_SHIFT_FIELDS', payload)
  },

  async getEstimatedLunches({ rootState }, { associate_number, shift_start, shift_end, lunch_waived, project_id }) {
    const timeZone = rootState.applicationState.timeZone

    try {
      const { data } = await apiClient.post('/api/get_estimated_lunch_time', {
        shift_start,
        associate_number,
        shift_end,
        lunch_waived,
        project_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return {
        estimated_lunch_end: data.estimated_lunch_end ? moment.utc(data.estimated_lunch_end).tz(timeZone) : undefined,
        estimated_lunch_start: data.estimated_lunch_start
          ? moment.utc(data.estimated_lunch_start).tz(timeZone)
          : undefined,
        estimated_second_lunch_end: data.estimated_second_lunch_end
          ? moment.utc(data.estimated_second_lunch_end).tz(timeZone)
          : undefined,
        estimated_second_lunch_start: data.estimated_second_lunch_start
          ? moment.utc(data.estimated_second_lunch_start).tz(timeZone)
          : undefined
      }
    } catch (error) {
      throw error.message
    }
  },

  async getEstimatedHours(
    _,
    { shift_id, start, end, lunch1_start, lunch1_end, lunch2_start, lunch2_end, calculation_type }
  ) {
    try {
      const payload = {
        shift_id,
        start,
        end,
        lunch1_start,
        lunch1_end,
        lunch2_start,
        lunch2_end
      }

      if (calculation_type) {
        payload.calculation_type = calculation_type
      }

      const { data } = await apiClient.post('/api/shift/hours_calculate', payload, { cancelPreviousRequests: true })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.hours
    } catch (error) {
      throw error.message
    }
  },

  async postAuthorizationCheck({ rootState, state }, { associate_number, shift }) {
    try {
      const { data } = await apiClient.post('/api/shift/authorization/check', {
        project_id: rootState.applicationState.currentProject.id,
        associate_number,
        shift: shift ?? state.shiftForm
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.authorizations
    } catch (error) {
      throw error.message
    }
  },

  async postShiftConflict({ rootState, state }, { associate_number }) {
    try {
      const { data } = await apiClient.post('/api/shift/conflict/check', {
        project_id: rootState.applicationState.currentProject.id,
        shift_id: state.shiftForm.id,
        start: moment(state.shiftForm.work_start).utc().toISOString(),
        end: moment(state.shiftForm.work_end).utc().toISOString(),
        associate_number
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.result
    } catch (error) {
      throw error.message
    }
  },

  async updateShift({ rootState }, { auto_sign, shift }) {
    try {
      const { data } = await apiClient.post('/api/shift/adjust_time', {
        project_id: rootState.applicationState.currentProject.id,
        position: {
          latitude: 0,
          longitude: 0,
          altitude: 0
        },
        auto_sign,
        shift
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? "An error occurred while adjusting the shift's times."} `)
      }
    } catch (error) {
      throw error.message
    }
  },

  async createShift({ rootState }, { associate_number, auto_sign, shift }) {
    try {
      const { data } = await apiClient.post(`/api/shift/manually_entered/create`, {
        project_id: rootState.applicationState.currentProject.id,
        associate_number,
        position: {
          latitude: 0,
          longitude: 0,
          altitude: 0
        },
        completed_authorizations: [],
        auto_sign,
        shift
      })

      if (data.error_code && data.error_code !== '0' && data.error_code !== '2') {
        throw new Error(`${data.msg} `)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async downloadReportShiftTimeCards(_, { shift_id }) {
    try {
      const { data } = await apiClient.get(`/api/report/shift/${shift_id}/shift_card/download`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.url
    } catch (error) {
      throw error.message
    }
  },

  async sendReportShiftTimeCards(_, { shift_id, email }) {
    try {
      const { data } = await apiClient.get(`/api/report/shift/${shift_id}/shift_card/send_email/${email}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async markShiftsAsPaid({ rootState }, payload) {
    try {
      const { data } = await apiClient.post('/api/shift/mark_paid', {
        ...payload,
        project_id: rootState.applicationState.currentProject.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while marking shifts as paid.'} `)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async updateShiftAttachments({ commit, state }, { files, shift_id }) {
    try {
      const { data } = await apiClient.put(`/api/shift/${shift_id}/attachments`, {
        files
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? "An error occurred while upload shift's attachments."} `)
      }

      const updatedShift = {
        ...state.shift,
        shift: { ...state.shift.shift, files: data.attached_files }
      }

      commit('setShift', updatedShift)
    } catch (error) {
      throw error.message
    }
  },

  async getShiftRetrieve(_, { shift_id }) {
    try {
      const { data } = await apiClient.get(`/api/shift/attach_data/${shift_id}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postShiftMove(_, payload) {
    try {
      const { data } = await apiClient.put(`/api/shift/move_to_another_associate`, {
        ...payload
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getShiftConfirmation(_, { id }) {
    try {
      const { data } = await apiClient.post(`/api/shift/confirmation/cdph530/get`, {
        id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.link
    } catch (error) {
      throw error.message
    }
  },

  async generateShiftConfirmation({ rootState }, { associate_ids, shifts }) {
    try {
      const project = rootState.applicationState.currentProject.id
      const { data } = await apiClient.get(`/api/shift/confirmation/cdph530/instance/project/${project}`, {
        params: { associate_ids, shifts }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getShiftAttachments({ state, commit }, { id }) {
    try {
      const { data } = await apiClient.get(`/api/shift/${id}/attachments`, {
        id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      if (state.shift?.shift?.id === id) {
        const updatedShift = {
          ...state.shift,
          shift: {
            ...state.shift.shift,
            files: data.files
          }
        }

        commit('setShift', updatedShift)
      }

      return data.files
    } catch (error) {
      throw error.message
    }
  },

  async updateShiftByAssociate(_, payload) {
    try {
      const { data } = await apiClient.post(`/api/shift/payment_info/update_by_associate`, {
        ...payload
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async previewUnpaidShiftRates(_, payload) {
    try {
      const { data } = await apiClient.post('/api/shift/payment_info/unpaid_rate/preview', payload)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      const previewError = new Error(error.response?.data?.msg ?? error.response?.data?.detail ?? error.message)

      previewError.status = error.response?.status
      previewError.data = error.response?.data

      throw previewError
    }
  },

  async applyUnpaidShiftRates(_, payload) {
    try {
      const { data } = await apiClient.post('/api/shift/payment_info/unpaid_rate/apply', payload)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      const applyError = new Error(error.response?.data?.msg ?? error.response?.data?.detail ?? error.message)

      applyError.status = error.response?.status
      applyError.data = error.response?.data

      throw applyError
    }
  },

  async getShiftHistory(_, { shift_id }) {
    try {
      const { data } = await apiClient.get('/api/shift/change_history', {
        params: { id: shift_id }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.history
    } catch (error) {
      throw error.message
    }
  },

  async getShiftNewHistoryEventTypes() {
    const { data } = await apiClient.get('/api/shift/new_change_history/event_types')

    if (data.error_code && data.error_code !== '0') {
      throw new Error(`${data.msg}`)
    }

    return data.event_types ?? []
  },

  async getShiftNewHistory(_, { shift_id, params }) {
    const { data } = await apiClient.get(`/api/shift/${shift_id}/new_change_history`, {
      params
    })

    if (data.error_code && data.error_code !== '0') {
      throw new Error(`${data.msg}`)
    }

    return data.change_history ?? []
  },

  async postSignDigitalCDPH({ rootState }, { shift_id, signature_id }) {
    try {
      const { data } = await apiClient.post(`/api/shift/${shift_id}/digital_cdph/sign`, {
        signature_id,
        supervisor_id: rootState.applicationState.associate.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async postImportClipboard({ rootState }, { invoice_file, create_payment, sync_cdph, not_update_shifts }) {
    const formData = new FormData()

    formData.append('project_id', rootState.applicationState.currentProject.id)
    formData.append('invoice_file', invoice_file)
    formData.append('create_payment', create_payment)
    formData.append('sync_cdph', sync_cdph)
    formData.append('not_update_shifts', not_update_shifts)

    try {
      const { data } = await apiClient.post(`/api/shift/process/clipboard_invoice`, formData)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postShiftNote({ commit, state }, { shift_id, note }) {
    try {
      const { data } = await apiClient.put(`/api/shift/${shift_id}/notes`, {
        note
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setShift', {
        ...state.shift,
        shift: {
          ...state.shift.shift,
          note
        }
      })
    } catch (error) {
      throw error.message
    }
  }
}
